import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import * as turf from '@turf/turf';
import { useTheme } from '@mui/styles';

import { CircularProgress, Typography } from "@mui/material";

import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { getSeismicEventColor } from './RTFOMapWithWellbore';


export const RTFOSeismicAmplitudeChart = ({ 
  gridView,
  showSpikeLines,
  showLegend,
  seismicEditMode,
  seismicAmplitudeEvents,
  setSeismicAmplitudeEvents,
  seismicDataType
}) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stageData = currentStage?.coldboreData;

  const [seismicAmplitudeData, setSeismicAmplitudeData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    
    const abortController = new AbortController();

    const fetchData = async () => {
      setIsLoadingData(true);
      setSeismicAmplitudeData([]);

      const location = `https://ssdprojects.s3.us-east-2.amazonaws.com/${currentStage?.name}_seismic.json`;

      try {
        console.log('Fetching seismic data for stage', currentStage?.name);
        fetch(location, { signal: abortController.signal })
          .then(res => res.json())
          .then(data => setSeismicAmplitudeData(data))
          .then(() => setIsLoadingData(false))
          .catch(error => {
            console.log(error);
            setIsLoadingData(false);
          });

      } catch (e) {
        setIsLoadingData(false);
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };

  }, [currentStage?.name])


  const seismicEventsData = useMemo(() => {
    try {
      const seismicEvents = [];
      const seismicRadius = 10; // cutoff radius in kilometers
  
      if (!currentRTFO?.seismicData?.[seismicDataType]) return [];

      const stageStartTimestamp = stageData?.[0]?.timestamp;
      const stageEndTimestamp = stageData?.[stageData?.length - 1]?.timestamp;

      const stageSeismicEvents = currentRTFO?.seismicData?.[seismicDataType]?.filter(e => e.time >= stageStartTimestamp && e.time <= stageEndTimestamp);

      for (let event of stageSeismicEvents) {
        try {
          const wellHeadCoords = turf.point([currentRTFO?.surveys?.wells?.[Object.keys(currentRTFO?.surveys?.wells)?.[0]]?.surfaceLon, currentRTFO?.surveys?.wells?.[Object.keys(currentRTFO?.surveys?.wells)?.[0]]?.surfaceLat]);
          const eventCoords = turf.point([event?.longitude, event?.latitude]);
          const distance = turf.distance(wellHeadCoords, eventCoords);

          if (distance > 0 && distance <= seismicRadius && event?.localMagnitude > 0) {
            seismicEvents.push({...event, distance});
          };

        } catch (error) {
          console.log(error);
          return [];
        };
      };
  
      return seismicEvents;
      
    } catch (error) {
      console.log(error);
      return [];
    };
  }, [currentRTFO?.seismicData, currentRTFO?.surveys?.wells, seismicDataType, stageData]);

  const data = useMemo(() => {
    if (!stageData || !stageData?.length > 0) return [];

    return getChartData();

    function getChartData() {
      try {

        const X = [];
        const YPressure = [];
        const YRate = [];
        const YRNP = [];
        const YPropConcSurf = [];
        const YCumPropSurf = [];
        const YPropConcBH = [];
        const YPressureDHTop = [];
        const YTemperatureDHTop = [];
        const XSeismic = [];
        const YSeismic = [];

        const XSeismicEvents = [];
        const YSeismicEvents = [];

        stageData?.forEach((d,i) => {
          const proppantMass = (((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - stageData?.[i-1]?.time))) * (d?.proppant || 0) / 1000) || 0;
          X.push(new Date(d.timestamp));
          YPressure.push(d.pressure || 0);
          YPropConcSurf.push(d.proppant || 0);
          YCumPropSurf.push(i === 0 ? proppantMass : (YCumPropSurf?.[i-1] + proppantMass));
          YPropConcBH.push(d.proppantBH || 0);
          YRate.push(d.rate || 0);
          YRNP.push((d.pressure > 0 && d.rate > 0) ? (d.pressure / d.rate * 2 / 3) : null); // Add the correction factor
          YPressureDHTop.push(d.dhPressureTop || null);
          YTemperatureDHTop.push(d.dhTemperatureTop || null);
        });

        seismicAmplitudeData?.forEach(d => {
          if (!isNaN(d?.y)) {
            XSeismic.push(d?.timestamp);
            YSeismic.push(d?.y);
          };
        });

        seismicEventsData?.forEach(d => {
          XSeismicEvents.push(d?.time);
          YSeismicEvents.push(d?.localMagnitude);
        });

        // add traces
        let treatingPressureTrace = {
          x: X,
          y: YPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Pressure (MPa)',
          line: {
            width: 2,
            color: chartColors[2]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let rateTrace = {
          x: X,
          y: YRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Rate (m3/min)',
          line: {
            width: 2,
            color: chartColors[3]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let rnpTrace = {
          x: X,
          y: YRNP,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Fracfeed',
          line: {
            width: 2,
            color: chartColors[1]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let propConcSurfTrace = {
          x: X,
          y: YPropConcSurf,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'Propp. Surf. (kg/m3)',
          line: {
            width: 2,
            color: chartColors[4]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let cumPropSurfTrace = {
          x: X,
          y: YCumPropSurf,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'Cum. Propp. (tons)',
          line: {
            width: 2,
            color: chartColors[4],
            dash: 'dash'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let propConcBHTrace = {
          x: X,
          y: YPropConcBH,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: 'Propp. BH (kg/m3)',
          line: {
            width: 2,
            color: chartColors[6]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let dhPressureTopTrace = {
          x: X,
          y: YPressureDHTop,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'BHP Top (MPa)',
          line: {
            width: 2,
            color: 'black'
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let dhTemperatureTopTrace = {
          x: X,
          y: YTemperatureDHTop,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'BHT Top (degC)',
          line: {
            width: 2,
            color: chartColors[12]
          },
          hoverinfo: !showSpikeLines && 'none'
        };
        let seismicTrace = {
          x: XSeismic,
          y: YSeismic,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: 'Y Amplitude (mm/s)',
          line: {
            width: 1,
            color: 'black'
          },
          opacity: 0.5,
          hoverinfo: !showSpikeLines && 'none',
          connectgaps: false
        };
        let seismicEventTrace = { // seismic event
          x: XSeismicEvents,
          y: YSeismicEvents,
          mode: 'markers',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y2',
          name: 'Seismic Event',
          marker: {
            size: 10,
            color: YSeismicEvents.map(val => getSeismicEventColor(val)),
            opacity: 1,
          },
          text: YSeismicEvents?.map(d => `Local Magnitude: ${d}`)
          // hovertemplate: 'Date: %{x}' +
          //               'Local Magnitude: %{y}'
        };

        let traces = [ 
          treatingPressureTrace, 
          rateTrace,
          rnpTrace,
          dhPressureTopTrace,
          dhTemperatureTopTrace,
          propConcSurfTrace,
          cumPropSurfTrace,
          propConcBHTrace,
          seismicTrace,
          seismicEventTrace
        ];

        if (!dhPressureTopTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0 || currentRTFO?.name !== '11-14A') {
          traces = traces.filter(t => t.name !== 'BHP Top (MPa)');
          traces = traces.filter(t => t.name !== 'BHT Top (degC)');
        };

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [stageData, seismicAmplitudeData, seismicEventsData, showSpikeLines, currentRTFO?.name]);

  const layout = {
    yaxis: { // Treating pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: 'Treating Pressure (MPa)',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: [0, 100],
      side: 'left'
    },
    yaxis2: { // Slurry rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: 'Slurry Rate (m3/min) and Fracfeed',
        font: {
          size: 12
        },
      },
      range: [0, 25],
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0
    },
    yaxis3: { // Prop conc surf y axis
      linecolor: chartColors[4],
      showgrid: false,
      color: chartColors[4],
      title: {
        text: 'Propp. Conc. and Cum. Propp. (kg/m3 or tons)',
        font: {
          size: 12
        },
      },
      range: [0, 1000],
      overlaying: 'y',
      side: 'right',
      anchor: 'x'
    },
    yaxis4: { // Seismic y axis
      linecolor: darkMode ? 'lightgrey' : 'grey',
      showgrid: false,
      color: darkMode ? 'lightgrey' : 'grey',
      title: {
        text: 'Y Seismic Amplitude (mm/s)',
        font: {
          size: 12
        },
      },
      overlaying: 'y',
      side: 'right',
      anchor: 'free',
      position: 1
    },
    xaxis: { // Date x axis
      autorange: true,
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      type: 'date',
    },
    hovermode: "x",
    hoverdistance: 20, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.15 : -0.1),
    },
    annotations: [
      {
        text: "Seismic Chart",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      }
    ],
    shapes: [
      
    ],
    uirevision: 'true',
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  if (seismicAmplitudeEvents?.length > 0) {
    const newLines = seismicAmplitudeEvents.filter(event => event?.stageName === currentStage?.name)?.map(event => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event.timestamp),
        y0: 0,
        x1: new Date(event.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      };
    });
    const newAnnotations = seismicAmplitudeEvents.filter(event => event?.stageName === currentStage?.name).map((event,i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: event?.name,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newLines);
    layout.annotations = layout.annotations.concat(newAnnotations);
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-seismic-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    },
    // modeBarButtonsToAdd: [
    //   "togglespikelines"
    // ]
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative'}}>
      {isLoadingData &&
        <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 420, backgroundColor: 'rgba(255, 255, 255, 0.75)'}}>
          <Typography style={{marginBottom: 20}}>Loading seismic data...</Typography>
          <CircularProgress
          />
        </div>
      }
      <Plot
        data={data}
        layout={layout}
        config={config}
        useResizeHandler={true}
        style={{width: '100%', height: '100%'}}
        onClick={handlePlotClick}
      />
      
    </div>
  );

  function handlePlotClick(e) {
    try {
      if (!seismicEditMode) return;

      // get a timestamp of the clicked point
      const newSeismicTimestamp = new Date(e.points?.find(point => point?.data?.name === 'Pressure (MPa)')?.x)?.getTime();
      
      let stageSeismicAmplitudeEvents = seismicAmplitudeEvents.filter(event => event?.stageName === currentStage?.name) || [];

      const existingTimestamp = stageSeismicAmplitudeEvents?.find(t => Math.abs(t?.timestamp - newSeismicTimestamp) <= 30000);


      if (existingTimestamp) {
        stageSeismicAmplitudeEvents = stageSeismicAmplitudeEvents?.filter(t => Math.abs(t?.timestamp - newSeismicTimestamp) > 30000);
      } else {
        stageSeismicAmplitudeEvents.push({timestamp: newSeismicTimestamp});
        stageSeismicAmplitudeEvents.sort((a, b) => a.timestamp - b.timestamp);
      };

      // Calculate cum. proppant
      const cumProppantArray = [];
      stageData?.forEach((d, i) => {
        const proppantMass = (((d?.clean || 0) * (i === 0 ? d?.time : (d?.time - stageData?.[i-1]?.time))) * (d?.proppant || 0) / 1000) || 0;
        cumProppantArray.push({
          timestamp: d.timestamp,
          cumProppant: i === 0 ? proppantMass : ((cumProppantArray?.[i-1]?.cumProppant || 0) + proppantMass)
        });
      });

      const startTimestamp = stageSeismicAmplitudeEvents?.[0]?.timestamp || stageData?.[0]?.timestamp;

      const newStageSeismicEvents = [];

      for (let i = 0; i < stageSeismicAmplitudeEvents.length; i++) {
        
        const eventTimestamp = stageSeismicAmplitudeEvents[i]?.timestamp;

        const eventData = stageData?.find(d => d.timestamp === eventTimestamp);

        // Calculate clean volume
        const slurryVolume = (eventData?.slurry - stageData?.find(d => d.timestamp === startTimestamp)?.slurry);
                
        // Calculate cum proppant
        const cumProppant = (cumProppantArray?.find(d => d.timestamp === eventTimestamp)?.cumProppant - cumProppantArray?.find(d => d.timestamp === startTimestamp)?.cumProppant);

        // Event data
        
        newStageSeismicEvents.push({
          name: i === 0 ? 'Stage Start' : `Seismic Event ${parseInt(i)}`,
          stageName: currentStage?.name,
          date: new Date(eventTimestamp)?.toLocaleString(),
          timestamp: eventTimestamp,
          deltaTime: i === 0 ? 0 : (eventTimestamp - startTimestamp) / 60000,
          pressure: eventData?.pressure || 0,
          rate: eventData?.rate || 0,
          proppant: eventData?.proppant || 0,
          slurryVolume,
          cumProppant
        });
      };

      const newSeismicEvents = seismicAmplitudeEvents?.filter(event => event?.stageName !== currentStage?.name)?.concat(newStageSeismicEvents)?.flat(Infinity)?.sort((a,b) => a.timestamp - b.timestamp);
      setSeismicAmplitudeEvents(newSeismicEvents);

    } catch (error) {
      console.log(error);
      return;
    };
  };
};
